/**
 * Today/Week segmented toggle for the Todo Calendar. Mirrors the look of
 * EventsGlassTabFilter (which hardcodes its own two tabs, so it is not
 * reusable) with clean, AA-safe tokens: tint fill + `interactive.primaryHover`
 * ink for the active tab (text.link measures under 4.5:1 on the tint in light),
 * `semantic.text.secondary` for inactive labels (text.dim fails on card
 * backgrounds in both themes).
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';

export type TodoCalendarMode = 'today' | 'week';

interface TodoCalendarTabsProps {
  mode: TodoCalendarMode;
  onModeChange: (mode: TodoCalendarMode) => void;
}

const TABS: Array<{ mode: TodoCalendarMode; label: string }> = [
  { mode: 'today', label: 'Today' },
  { mode: 'week', label: 'Week' },
];

export const TodoCalendarTabs: React.FC<TodoCalendarTabsProps> = React.memo(
  ({ mode, onModeChange }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        container: {
          flexDirection: 'row',
          borderWidth: 1,
          borderRadius: radius.md,
          padding: space.xs,
          backgroundColor: colors.card.bg,
          borderColor: colors.card.border,
        },
        tab: {
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          height: 38,
          borderRadius: radius.sm,
        },
        tabActive: {
          backgroundColor: colors.accent.blueBg,
        },
        label: {
          ...type.button,
          color: semantic.text.secondary,
        },
        labelActive: {
          color: semantic.interactive.primaryHover,
        },
      }),
    );

    return (
      <View style={styles.container}>
        {TABS.map(tab => {
          const isActive = tab.mode === mode;
          return (
            <TouchableOpacity
              key={tab.mode}
              style={[styles.tab, isActive && styles.tabActive]}
              onPress={() => onModeChange(tab.mode)}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
            >
              <Text style={[styles.label, isActive && styles.labelActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  },
);

TodoCalendarTabs.displayName = 'TodoCalendarTabs';
